import { Link } from "react-router";
import type { OrderView } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { useMyOrdersQuery } from "../../api/queries";
import { KIND_LABEL, ORDER_STATUS_LABEL, ORDER_STATUS_TONE } from "../../commerce/orderStatus";
import { formatCents, formatDateTime } from "../../lib/format";

/** One row of "My orders" (presentational; server-rendered in tests). */
export function OrderRow({ order }: { order: OrderView }) {
  return (
    <li className="order-row" data-testid={`order-row-${order.orderId}`}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Link to={`/orders/${order.orderId}`}>
          <strong>{order.description || KIND_LABEL[order.kind]}</strong>
        </Link>
        <div className="small muted">
          {KIND_LABEL[order.kind]} · {formatDateTime(order.createdAt)}
          {order.receiptNumber ? ` · receipt ${order.receiptNumber}` : ""}
        </div>
        <div className="small price">
          {formatCents(order.subtotalCents)}
          {order.taxCents > 0 ? ` + ${formatCents(order.taxCents)} sales tax` : ""}: {formatCents(order.creditAppliedCents)} Townsy Credit +{" "}
          {formatCents(order.cardAmountCents)} {order.mode === "live" ? "card" : "card (test)"}
        </div>
      </div>
      <span className={`badge order-badge order-badge-${ORDER_STATUS_TONE[order.status]}`}>{ORDER_STATUS_LABEL[order.status]}</span>
    </li>
  );
}

/**
 * The account's dollar orders, newest first (`GET /me/orders`). Each links to its receipt page.
 * Coin purchases are not orders here: they are listed in the wallet's coin history.
 */
export function MyOrdersList({ signedIn, limit }: { signedIn: boolean; limit?: number }) {
  const q = useMyOrdersQuery(signedIn);
  if (!signedIn) {
    return (
      <p className="muted small" style={{ margin: 0 }}>
        <Link to="/account">Sign in</Link> to see your orders.
      </p>
    );
  }
  if (q.isPending) return <p className="muted small">Loading your orders…</p>;
  if (q.error) {
    return (
      <p className="small error-text" role="alert">
        Couldn't load your orders: {errorMessage(q.error)}{" "}
        <button type="button" className="btn btn-sm" onClick={() => void q.refetch()}>
          Retry
        </button>
      </p>
    );
  }
  const all = q.data?.pages.flatMap((p) => p.items) ?? [];
  const items = limit ? all.slice(0, limit) : all;
  if (items.length === 0) {
    return (
      <p className="muted small" style={{ margin: 0 }} data-testid="orders-empty">
        No dollar purchases yet. Coin purchases are listed in your wallet's coin history.
      </p>
    );
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <ul className="order-list" data-testid="my-orders">
        {items.map((o) => (
          <OrderRow key={o.orderId} order={o} />
        ))}
      </ul>
      {!limit && q.hasNextPage ? (
        <button type="button" className="btn btn-sm" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
          {q.isFetchingNextPage ? "Loading…" : "Load older"}
        </button>
      ) : null}
    </div>
  );
}
